import { http, DEFAULT_TOKEN, getAuthToken } from './client';
import { useConfig } from '../hooks/useConfig';

export interface OrganizationItem {
  id: string;
  name: string;
  atomicCount: number;
  createTime: string;
}

/** 获取组织列表，可选 status 过滤（如 'enabled'）。管理列表不传，运行场景任务侧只取启用的。 */
export async function getOrganizationList(params?: {
  status?: string;
}): Promise<OrganizationItem[]> {
  const response = await http.get<OrganizationItem[]>(
    '/ai-api/holon/v1/organizations',
    { params: params?.status ? { status: params.status } : undefined }
  );

  return response.data || [];
}

/** 单个组织详情中的成员节点（递归结构） */
export type OrganizationMemberType = 'person' | 'department';

/** 公司 owner 信息：仅根节点可能携带。 */
export interface OrganizationOwnerInfo {
  name: string;
  roles?: string[];
}

export interface OrganizationMemberNode {
  id?: string;
  name: string;
  /** 后端新结构会显式返回节点类型；旧数据没有时前端继续按既有规则兜底判断。 */
  type?: OrganizationMemberType;
  /** 人员头像 URL（可选）；有值时组织图优先显示真实头像。 */
  avatar?: string;
  /** 公司根节点可能携带 owner，用于补充显示公司负责人。 */
  owner?: OrganizationOwnerInfo;
  /** 人员节点会有 roles；部门等分组节点没有 */
  roles?: string[];
  /** 部门/公司会有 members；叶子人员没有 */
  members?: OrganizationMemberNode[];
}

/** 单个组织详情中的角色定义 */
export interface OrganizationRoleDefinition {
  name: string;
  /** 角色可以执行的动作列表 */
  actions?: string[];
}

/** 单个组织详情响应 */
export interface OrganizationDetail {
  roles: OrganizationRoleDefinition[];
  organization: OrganizationMemberNode;
}

/**
 * 查询单个组织详情
 * GET /ai-api/holon/v1/organizations/{organizationId}
 * 返回完整的角色定义与组织架构树，用于组织架构图渲染
 */
export async function getOrganizationDetail(
  organizationId: string
): Promise<OrganizationDetail> {
  const response = await http.get<OrganizationDetail>(
    `/ai-api/holon/v1/organizations/${organizationId}`
  );
  return response.data;
}

// 鉴权 token：默认使用 DEFAULT_TOKEN，需要登录 token 时使用 getAuthToken()

/**
 * 流式创建组织
 * POST /ai-api/holon/v1/organizations，请求体为组织 JSON 数据
 * 返回 ReadableStream body 供调用方逐行解析 JSONL
 */
export async function createOrganizationStream(
  jsonData: unknown,
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const { serverConfig } = useConfig();
  const baseUrl = (
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    'https://holon.aialign.cn'
  ).replace(/\/$/, '');

  const fetchOptions: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      Authorization: `Bearer ${DEFAULT_TOKEN}`,
    },
    body: JSON.stringify(jsonData),
  };

  if (abortController) {
    fetchOptions.signal = abortController.signal;
  }

  const response = await fetch(
    `${baseUrl}/ai-api/holon/v1/organizations`,
    fetchOptions
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}

/**
 * 流式解散组织
 * DELETE /ai-api/holon/v1/organizations/{organizationId}
 * 返回 ReadableStream body 供调用方逐行解析 JSONL
 */
export async function dissolveOrganizationStream(
  organizationId: string,
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const { serverConfig } = useConfig();
  const baseUrl = (
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    'https://holon.aialign.cn'
  ).replace(/\/$/, '');

  const fetchOptions: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      Authorization: `Bearer ${DEFAULT_TOKEN}`,
    },
  };

  if (abortController) {
    fetchOptions.signal = abortController.signal;
  }

  const response = await fetch(
    `${baseUrl}/ai-api/holon/v1/organizations/${organizationId}`,
    fetchOptions
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}

/**
 * 流式运行场景任务
 * POST /ai-api/holon/v1/organizations/{organizationId}/processes
 * 返回 ReadableStream body 供调用方逐行解析 JSONL
 */
export async function runScenarioTaskStream(
  organizationId: string,
  body: { parameters: Record<string, unknown>; process: unknown },
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const { serverConfig } = useConfig();
  const baseUrl = (
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    'https://holon.aialign.cn'
  ).replace(/\/$/, '');

  const fetchOptions: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      Authorization: `Bearer ${DEFAULT_TOKEN}`,
    },
    body: JSON.stringify(body),
  };

  if (abortController) {
    fetchOptions.signal = abortController.signal;
  }

  const response = await fetch(
    `${baseUrl}/ai-api/holon/v1/organizations/${organizationId}/processes`,
    fetchOptions
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}

/**
 * 暂停后继续运行场景任务
 * PUT /ai-api/holon/v1/organizations/{organizationId}/processes/{requestId}/resume
 */
export async function resumeScenarioTaskProcess(
  organizationId: string,
  requestId: string,
  knowledgeBaseId: string
): Promise<void> {
  await http.put(
    `/ai-api/holon/v1/organizations/${organizationId}/processes/${requestId}/resume`,
    {
      key: '知识库ID',
      parameters: {
        知识库ID: knowledgeBaseId,
      },
    }
  );
}
